import React from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { withNamespaces } from 'react-i18next';
import styled from 'styled-components';

import { Link } from 'core/routing';
import { ComponentRegistry } from 'core/index';
import { Button, Avatar, Tooltip } from 'components';

import DocumentHead from 'legacyComponents/documentHead';
import { media } from 'legacyComponents/styled/styleUtils';
import { TopBar } from 'legacyComponents/layout/topBar';
import { CardSection } from 'legacyComponents/layout/cardSection';
import { ProfileIndicator } from 'legacyComponents/layout/profileIndicator';
import { ProjectCard } from 'legacyComponents/cards/types/project';
import { StoryCard } from 'legacyComponents/cards/types/story';
import { ThreadCard } from 'legacyComponents/cards/types/thread';
import { CollectionCard } from 'legacyComponents/cards/types/collection';

import { imageSrc } from 'apps/file/utils';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import { conf as globalConf } from 'apps/config.yaml';
import {
  MainWrapper,
  ProfileNoContent,
  Indicator,
} from 'legacyComponents/styled/lib';

import Header from 'apps/components/header/header';
import { Tags, TagsIndicator } from 'apps/tag/index';
import { FourOhFour } from 'apps/fourOhFour/index';
import { CollectionForm } from 'apps/collection/collectionForm/index';

import { ProfileAppManager } from 'apps/profile/main/appManager';
import { ProfileOverview } from 'apps/profile/views/overview';
import { ProfilesAutoComplete } from 'apps/profile/views/autocomplete';
import { ProfileItem } from 'apps/profile/views/item';
import { conf } from 'apps/profile/config.yaml';

ComponentRegistry.add('profiles.itemComponent', ProfileItem);
ComponentRegistry.add('profile.autocompleteElement', ProfilesAutoComplete);

@withNamespaces()
@inject('routerStore')
@observer
export class Profile extends React.Component {
  UNSAFE_componentWillMount() {
    this.appManager = new ProfileAppManager(null, this.props);
  }

  componentWillUnmount = () => {
    clientStore.SetFooterHidden(false);
  };

  render() {
    const manager = this.appManager;
    if (manager.queries.get('profile')?.notFound) {
      return <FourOhFour type='Profile' />;
    }
    const {
      routerStore: { params },
      t,
    } = this.props;
    const {
      profile,
      projectsConn,
      storiesConn,
      collectionsConn,
      threadsConn,
    } = manager;
    const projects = projectsConn.arrayApp;
    const stories = storiesConn.arrayApp;
    const collections = collectionsConn.arrayApp;
    const threads = threadsConn.arrayApp;
    const { username } = profile?.user || '';
    const dataTip = this.props.t(
      'See all Organisations that {{ username }} is part of',
      {
        username,
      },
    );

    if (profile === null) {
      if (manager.loading) {
        return (
          <main>
            <DocumentHead title={`${params.get('profile')}`} />
            <Header />
            <TopBar>
              <TagsIndicator />
              <AvatarWrapper>
                <AvatarIndicator />
                <AvatarIndicator />
                <AvatarIndicator />
              </AvatarWrapper>
            </TopBar>
            <ProfileIndicator />
          </main>
        );
      }
      // TODO: this should be some kind of error page. Profile == null but
      // the manager is not loading (not going to change).
      return null;
    }
    const { canUpdate, fullName, skills } = profile;
    const {
      inSpace: { whichTypes },
    } = profile;
    const displayName = fullName.split(' ')[0];
    const profileInitiatives = Array.from(
      manager.initiativesConn.arrayApp.apps.values(),
    );
    const displayInitiatives = profileInitiatives.slice(0, 3);
    const showMoreFunction = profile.ShowMembers || profile.ShowInitiatives;

    const noProjects = !canUpdate && projectsConn.totalCount === 0;
    const noStories = !canUpdate && storiesConn.totalCount === 0;
    const noCollections = !canUpdate && collectionsConn.totalCount === 0;
    const noThreads = !canUpdate && threadsConn.arrayApp.length === 0;

    const noContent = [];
    if (noProjects && noStories && noCollections && noThreads) {
      clientStore.SetFooterHidden(true);
      if (!canUpdate) {
        noContent.push(
          <ProfileNoContent key={`${profile.fullName}-no-content`}>
            {profile.fullName.split(' ')[0]} is just getting started on
            Wikifactory. Follow or check back later for Projects, Stories and
            Collections!
          </ProfileNoContent>,
        );
      }
    }

    return (
      <>
        <DocumentHead
          title={`${profile.fullName} (${params.get('profile')})`}
          description={profile.bio}
          imagePath={imageSrc(
            profile.avatar,
            '',
            globalConf.imgSizes.profile.large,
          )}
        />
        <Header />
        <TopBar>
          <Tooltip delayShow={500} />
          <Tags type='Skills' tags={skills} app={profile} />
          <Initiatives>
            {profileInitiatives.length > 3 && (
              <Initiative
                onClick={showMoreFunction}
                condition={profileInitiatives.length > 3}
                data-tip={dataTip}
              >
                <MoreInitiatives>+{profileInitiatives.length}</MoreInitiatives>
              </Initiative>
            )}
            {displayInitiatives.map((initiative, index) => {
              const tooltip = `${initiative.title} (${initiative.slug})`;
              const linkName = 'initiative';
              const linkParams = { initiative: initiative.slug };
              return (
                <Initiative key={index} data-tip={tooltip}>
                  <Link name={linkName} params={linkParams}>
                    <InitiativeAvatar
                      avatar={initiative.avatar?.url}
                      name={initiative.title}
                    />
                  </Link>
                </Initiative>
              );
            })}
          </Initiatives>
        </TopBar>
        <MainWrapper>
          <ProfileOverview profileManager={this.appManager} />
          <CardSection
            invisible={manager.loading}
            title={conf.sections[0]}
            tooltip={t('Create new project')}
            updateHeader={true}
            index={1}
            createHandler={
              whichTypes.includes('Space')
                ? () => clientStore.routerStore.navigate('home.newProject')
                : () => ''
            }
            owner={canUpdate}
            ownerName={displayName}
            cardApp={projects}
            CardComponent={ProjectCard}
            noDataCardType='project'
            last={noStories && noCollections && noThreads}
            gridColumns={3}
          />
          <CardSection
            invisible={manager.loading}
            title={conf.sections[1]}
            tooltip={t('Create new story')}
            updateHeader={true}
            index={2}
            createHandler={
              whichTypes.includes('Story')
                ? () => clientStore.routerStore.navigate('home.newStory')
                : () => ''
            }
            owner={canUpdate}
            ownerName={displayName}
            cardApp={stories}
            CardComponent={StoryCard}
            noDataCardType='story'
            last={noCollections && noThreads}
            gridColumns={2}
          />
          <CardSection
            title='Threads'
            subTitle={' '}
            owner={canUpdate}
            ownerName={displayName}
            cardApp={threads}
            CardComponent={ThreadCard}
            noDataCardType='forumThreads'
            last={noCollections}
          />
          <CardSection
            title={conf.sections[2]}
            tooltip={t('Create new collection')}
            updateHeader={true}
            index={3}
            createHandler={
              whichTypes.includes('Collection')
                ? () => modalStore.SetModalComponent(CollectionForm)
                : () => ''
            }
            owner={canUpdate}
            ownerName={displayName}
            cardApp={collections}
            CardComponent={CollectionCard}
            noDataCardType='collection'
            gridColumns={3}
            last
          />
        </MainWrapper>
        {noContent}
      </>
    );
  }
}

const Initiative = styled.li`
  display: inline-block;
  &:not(:first-child) {
    margin-left: ${props => props.theme.spacing.default};
  }
`;

const Initiatives = styled.ul`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  ${media.s`
    display: none;
  `}
`;

const InitiativeAvatar = styled(Avatar)`
  width: 34px;
  height: 34px;
  cursor: pointer;
  > img {
    height: 100%;
    width: 100%;
  }
  &:hover {
    opacity: 0.9;
  }
  ${media.s`
    width: 30px;
    height: 30px;
  `}
`;

const MoreInitiatives = styled(InitiativeAvatar)`
  background-image: none;
  background-color: rgba(0, 0, 0, 0.2);
  color: ${props => props.theme.color.wfWhite};
  font-weight: ${props => props.theme.font.weight.xbold};
  font-size: ${props => props.theme.font.size.small};
`;

export const AvatarWrapper = styled.div`
  float: right;
  margin-top: 0px;
`;

export const AvatarIndicator = styled(Indicator)`
  width: 34px;
  height: 34px;
  border-radius: 100%;
  margin-left: 10px;
  display: inline-block;
  ${media.s`
    display: none;
  `}
`;

Profile.propTypes = {
  routerStore: PropTypes.object,
  routeName: PropTypes.string,
  params: PropTypes.object,
  t: PropTypes.func,
};
